import { useCallback, useEffect, useMemo, useState } from "react";

import { observer } from "mobx-react-lite";
import { nanoid } from "nanoid";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";

import { NodeFieldLabel } from "@/components/doc/right-sidebar/active-node-style/fields/NodeTextField";
import { appendBlock } from "@/components/doc/right-sidebar/create-blocks/utils/appendBlock";
import { InlineNodeComponent } from "@/components/expression/InlineNode";
import { NodeStyleProvider } from "@/components/expression/utils/NodeStyleProvider";
import { FormButton } from "@/components/form/FormButton";
import { Icon } from "@/components/icons/Icon";
import { pyodideManager } from "@/components/pyodide/PyodideManager";
import { ActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { twMerge } from "@/utils/twMerge";

import { ChartCustomization } from "./ChartCustomization";
import { VIZ_TYPES_1D, VIZ_TYPES_2D } from "./constants";
import { DataAnalysisResult } from "./DataAnalysisResult";
import {
  applyChartOptions,
  DEFAULT_CHART_OPTIONS,
  getActiveDataSource,
  isAllNumericMatrix,
  isChartResult,
  type AnalysisType,
  type ChartOptions,
  type ChartTheme,
  type DataSource,
} from "./dataAnalysisUtils";
import { TableDataPreview } from "./TableDataPreview";
import { useDataAnalysis } from "./useDataAnalysis";

import type { DocumentModel } from "@/models/DocumentModel";

function getSourceKey(source: DataSource | null): string | null {
  if (!source) return null;
  if (source.kind === "tiptap-table") return `table-${source.pos}`;
  return `${source.kind}-${source.node.id}`;
}

type DataAnalysisTab = "statistics" | "visualizations";

export const DataAnalysisPanel = observer(function DataAnalysisPanel({
  doc,
}: {
  doc: DocumentModel;
}) {
  const t = useTranslations("sidebar.activeNode.dataAnalysis");
  const { theme, systemTheme } = useTheme();
  const currentTheme: ChartTheme =
    (theme === "system" ? systemTheme : theme) === "light" ? "light" : "dark";

  const source = getActiveDataSource(doc);
  const [chartOptions, setChartOptions] = useState<ChartOptions>({ ...DEFAULT_CHART_OPTIONS });
  const { result, isLoading, error, runAnalysis } = useDataAnalysis(source, currentTheme);
  const [activeTab, setActiveTab] = useState<DataAnalysisTab>("statistics");
  const [activeType, setActiveType] = useState<AnalysisType>("descriptive");

  // Cosmetic options are applied at render time, so no re-computation is needed.
  const displayResult = useMemo(() => {
    if (!result || !isChartResult(result)) return result;
    return {
      ...result,
      data: applyChartOptions(result.data, chartOptions),
    } as typeof result;
  }, [result, chartOptions]);

  const handleOptionsChange = useCallback(
    (patch: Partial<ChartOptions>) => {
      setChartOptions((prev) => {
        const next = { ...prev, ...patch };
        // Re-run analysis only if computation-affecting options changed
        if (
          (patch.polyDegree !== undefined && patch.polyDegree !== prev.polyDegree) ||
          (patch.histogramBins !== undefined && patch.histogramBins !== prev.histogramBins)
        ) {
          runAnalysis(activeType, next);
        }
        return next;
      });
    },
    [runAnalysis, activeType],
  );

  const sourceKey = getSourceKey(source);
  const is2D = useMemo(() => (source ? isAllNumericMatrix(source) : false), [sourceKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Statistics is a view, not an action, so compute it eagerly once the engine is ready.
  const pyodideReady = pyodideManager.isReady;
  useEffect(() => {
    if (activeTab !== "statistics" || !source || !pyodideReady) return;
    if (result?.type === "descriptive" || isLoading) return;
    runAnalysis("descriptive");
  }, [activeTab, sourceKey, pyodideReady, result, isLoading, runAnalysis]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!source) {
    return null;
  }

  const vizTypes = is2D ? VIZ_TYPES_2D : VIZ_TYPES_1D;

  const handleSelect = (type: AnalysisType) => {
    setActiveType(type);
    const defaults = { ...DEFAULT_CHART_OPTIONS };
    setChartOptions(defaults);
    runAnalysis(type, defaults);
  };

  const handleInsertChart = () => {
    if (!displayResult || !isChartResult(displayResult)) return;

    appendBlock(doc, "plotlyChart", {
      chartId: nanoid(),
      plotlyData: displayResult.data.plotlyData,
      plotlyLayout: displayResult.data.plotlyLayout,
      chartType: displayResult.data.chartType,
    });
  };

  return (
    <ActionsDisclosure
      data-panel="data_analysis_panel"
      title={t("title")}
      disclosureProps={{ defaultOpen: true }}
      className="w-full"
    >
      <NodeStyleProvider renderContext="right-sidebar" size="sm">
        <NodeFieldLabel
          as="div"
          className="p-1 mb-2 min-h-[28px] flex-col items-start shadow-block dark:shadow-block-inverted overflow-auto no-scrollbar cursor-default"
        >
          {source.kind === "tiptap-table" ? (
            <TableDataPreview source={source} doc={doc} />
          ) : (
            <InlineNodeComponent
              doc={doc}
              nodes={[source.node]}
              className="pointer-events-none font-mono opacity-60"
            />
          )}
        </NodeFieldLabel>
      </NodeStyleProvider>

      <div className="flex gap-1 mb-2">
        {(["statistics", "visualizations"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={twMerge(
              "flex-1 px-2 py-1.5 text-xxxs font-mono uppercase tracking-tight rounded-6 transition-colors",
              activeTab === tab
                ? "bg-document-bg shadow-block dark:shadow-block-inverted text-gray-900 dark:text-gray-100"
                : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300",
            )}
          >
            {t(`tabs.${tab}`)}
          </button>
        ))}
      </div>

      {/* Only Visualizations needs a type picker; Statistics has a single view. */}
      {activeTab === "visualizations" && (
        <div className="flex flex-wrap gap-1 mb-2">
          {vizTypes.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleSelect(type)}
              className={twMerge(
                "flex-1 min-w-[60px] px-1.5 py-1.5 text-xxxs font-mono uppercase tracking-tight rounded-6 transition-colors",
                activeType === type && result?.type === type
                  ? "bg-document-bg shadow-block dark:shadow-block-inverted text-gray-900 dark:text-gray-100"
                  : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300",
              )}
            >
              {t(`actions.${type}`)}
            </button>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="text-xxxs font-mono text-gray-400 px-1 py-2">{t("computing")}</div>
      )}

      {error && <div className="text-xxxs font-mono text-red-500 px-1 py-2">{error}</div>}

      {displayResult && !isLoading && <DataAnalysisResult result={displayResult} />}

      {displayResult && !isLoading && isChartResult(displayResult) && (
        <>
          <ChartCustomization
            options={chartOptions}
            onChange={handleOptionsChange}
            activeType={activeType}
          />
          <FormButton
            onClick={handleInsertChart}
            variant="outline"
            className="flex items-center justify-center w-full h-8.5 mt-2 border-gray-300 dark:border-gray-700 rounded-6"
          >
            <Icon name="Import" className="mr-1" />
            {t("actions.insertChart")}
          </FormButton>
        </>
      )}
    </ActionsDisclosure>
  );
});
